import { useState } from 'react'
import clsx from 'clsx'
import {
  ArrowLeft, Activity, MonitorSmartphone, MapPin, AppWindow, ShieldCheck, ListChecks, FolderOpen, Settings,
  Eye, MousePointer2, Crosshair, Lock, RotateCw, MessageSquare, Trash2, Loader2, Bell, Wifi, WifiOff,
  History, KeyRound, LogIn, PlayCircle, Accessibility,
} from 'lucide-react'
import { useMdmState, useSendMdmCommand, useMdmWipe } from '../../../hooks/useMdm'
import { useRole } from '../../../hooks/useRole'
import DeviceStatusBadge from '../../../components/DeviceStatusBadge'
import DeviceKindIcon from '../../../components/DeviceKindIcon'
import ConfirmModal from '../../../components/Modal/ConfirmModal'
import EditDeviceSection from '../components/sections/EditDeviceSection'
import LogsSection from '../components/sections/LogsSection'
import DangerZoneSection from '../components/sections/DangerZoneSection'
import FieldsModal from './FieldsModal'
import OverviewTab from './OverviewTab'
import RemoteScreen from './RemoteScreen'
import LocationTab from './LocationTab'
import AppsTab from './AppsTab'
import PoliciesTab from './PoliciesTab'
import CommandsTab from './CommandsTab'
import FilesTab from './FilesTab'
import EventsTab from './EventsTab'
import LocalCodeModal from './LocalCodeModal'
import { BatteryIndicator, NetworkIndicator } from './mobileUi'
import {
  timeAgo, errorMessage, localStateBadges, isKioskSuspended, isMdmPaused, MESSAGE_FIELDS, messagePayload,
} from './mobileUtils'

const TABS = [
  { id: 'overview', icon: Activity, label: 'Visão geral' },
  { id: 'screen', icon: MonitorSmartphone, label: 'Tela remota' },
  { id: 'location', icon: MapPin, label: 'Localização' },
  { id: 'apps', icon: AppWindow, label: 'Apps' },
  { id: 'policies', icon: ShieldCheck, label: 'Políticas' },
  { id: 'commands', icon: ListChecks, label: 'Comandos' },
  { id: 'events', icon: History, label: 'Histórico' },
  { id: 'files', icon: FolderOpen, label: 'Arquivos' },
  { id: 'settings', icon: Settings, label: 'Configurações' },
]

/**
 * Console MDM de um tablet/celular (tipo "mobile"). Substitui o detalhe genérico
 * de ManagedDeviceDetail para kinds AndroidTablet/AndroidPhone.
 */
export default function MobileDeviceConsole({ device, onBack }) {
  const { isViewer } = useRole()
  const readOnly = isViewer
  const { data: state, error: stateError } = useMdmState(device.id)
  const cmd = useSendMdmCommand(device.id)
  const wipe = useMdmWipe(device.id)

  const [activeTab, setActiveTab] = useState('overview')
  const [screenControl, setScreenControl] = useState(false)
  const [modal, setModal] = useState(null) // 'message' | 'wipe' | 'reboot' | 'code'
  const [feedback, setFeedback] = useState(null)

  const run = async (type, payload, okText) => {
    setFeedback(null)
    try {
      await cmd.mutateAsync({ type, payload })
      setFeedback({ ok: true, text: okText })
      setModal(null)
    } catch (e) {
      setFeedback({ ok: false, text: errorMessage(e, 'Falha ao enviar o comando.') })
    }
  }

  const runWipe = async ({ reason }) => {
    setFeedback(null)
    try {
      await wipe.mutateAsync(reason?.trim() || 'Solicitado pelo painel')
      setFeedback({ ok: true, text: 'Wipe solicitado. O tablet será restaurado de fábrica assim que receber o comando.' })
      setModal(null)
    } catch (e) {
      setFeedback({ ok: false, text: errorMessage(e, 'Falha ao solicitar o wipe.') })
    }
  }

  const openScreen = (control) => {
    setScreenControl(control)
    setActiveTab('screen')
  }

  const s = state || {}
  const agentConnected = s.agentConnected
  const badges = localStateBadges(state)
  const kioskSuspended = isKioskSuspended(state)
  const mdmPaused = isMdmPaused(state)
  const allowRemove = state?.policy?.localAdmin?.allowRemove === true

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <button onClick={onBack} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar para a frota
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <DeviceKindIcon kind={device.kind} kindIconFileName={device.kindIconFileName} customIconBase64={device.customIconBase64} size={28} />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 truncate">{device.name}</h1>
            <p className="text-sm text-gray-500 truncate">
              {[s.manufacturer, s.model || device.model].filter(Boolean).join(' ') || device.kindName || device.kind}
              {s.androidVersion ? ` · Android ${s.androidVersion}` : ''}
            </p>
          </div>
          <DeviceStatusBadge status={s.status || device.status} />
          <span className={clsx('inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full', agentConnected ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500')}
            title="Canal em tempo real do agente">
            {agentConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {agentConnected ? 'Agente conectado' : 'Agente fora do ar'}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-sm">
          <BatteryIndicator level={s.batteryLevel} charging={s.charging} />
          <NetworkIndicator type={s.networkType} signalLevel={s.signalLevel} />
          <span className="text-gray-600" title={device.lastSeenAt || ''}>Visto {timeAgo(s.lastSeenAt || device.lastSeenAt)}</span>
          {device.vpnIp && <span className="text-gray-600 font-mono text-xs">VPN {device.vpnIp}</span>}
          {s.kioskActive && <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700">Quiosque ativo</span>}
          {s.accessibilityEnabled === false && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800" title="Sem acessibilidade o controle remoto não funciona">
              Acessibilidade desativada
            </span>
          )}
          {badges.map((b) => (
            <span key={b.key} className={clsx('px-2 py-0.5 text-xs rounded-full', b.className)} title={b.title}>{b.label}</span>
          ))}
        </div>

        {/* Ações rápidas */}
        <div className="flex flex-wrap gap-2 mt-4">
          {!readOnly && kioskSuspended && (
            <QuickAction icon={LogIn} label="Reativar quiosque" highlight disabled={cmd.isPending}
              onClick={() => run('kiosk-resume', null, 'Pedido enviado: o tablet volta ao quiosque.')} />
          )}
          {!readOnly && s.accessibilityEnabled === false && (
            // Agente ≥ 1.1.1 (versionCode 1001001) mostra no tablet a tela que guia quem está lá (CONTRACT §12.8).
            <QuickAction icon={Accessibility} label="Pedir acessibilidade" highlight
              disabled={cmd.isPending || (s.agentVersionCode ?? 0) < 1001001}
              title={(s.agentVersionCode ?? 0) < 1001001 ? 'Precisa do agente 1.1.1 ou mais novo no tablet' : 'Mostra no tablet a tela para ligar a acessibilidade do agente'}
              onClick={() => run('request-accessibility', null, 'Pedido enviado: o tablet mostra como ligar a acessibilidade.')} />
          )}
          {!readOnly && mdmPaused && (
            <QuickAction icon={PlayCircle} label="Retomar MDM" highlight disabled={cmd.isPending}
              onClick={() => run('mdm-resume', null, 'Pedido enviado: o MDM volta a valer no tablet.')} />
          )}
          <QuickAction icon={Eye} label="Ver tela" onClick={() => openScreen(false)} />
          {!readOnly && (
            <>
              <QuickAction icon={MousePointer2} label="Controlar" onClick={() => openScreen(true)} />
              <QuickAction icon={Crosshair} label="Localizar" disabled={cmd.isPending}
                onClick={() => { run('locate', null, 'Pedido de localização enviado.'); setActiveTab('location') }} />
              <QuickAction icon={Lock} label="Bloquear" disabled={cmd.isPending}
                onClick={() => run('lock', null, 'Tela bloqueada.')} />
              <QuickAction icon={RotateCw} label="Reiniciar" disabled={cmd.isPending} onClick={() => setModal('reboot')} />
              <QuickAction icon={Bell} label="Tocar alarme" disabled={cmd.isPending}
                onClick={() => run('ring', { seconds: 30 }, 'Alarme tocando por 30 s.')} />
              <QuickAction icon={MessageSquare} label="Mensagem" onClick={() => setModal('message')} />
              <QuickAction icon={KeyRound} label="Código de acesso" onClick={() => setModal('code')} />
              <QuickAction icon={Trash2} label="Wipe" danger onClick={() => setModal('wipe')} />
            </>
          )}
          {(cmd.isPending || wipe.isPending) && <Loader2 className="w-4 h-4 animate-spin text-gray-400 self-center" />}
        </div>

        {feedback && (
          <p className={clsx('mt-3 text-sm', feedback.ok ? 'text-green-700' : 'text-red-600')}>{feedback.text}</p>
        )}
        {stateError && !state && (
          <p className="mt-3 text-sm text-amber-700">Dados do MDM indisponíveis: {errorMessage(stateError)}</p>
        )}
      </div>

      {/* Abas */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6 overflow-x-auto">
          {TABS.map(({ id, icon: Icon, label }) => {
            const isActive = activeTab === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center whitespace-nowrap',
                  isActive ? 'tab-active-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                <Icon className={clsx('w-4 h-4 inline mr-2', isActive ? 'text-theme-secondary' : 'text-gray-400')} />
                {label}
              </button>
            )
          })}
        </nav>
      </div>

      {activeTab === 'overview' && <OverviewTab device={device} state={state} />}
      {activeTab === 'screen' && <RemoteScreen deviceId={device.id} initialControl={!readOnly && screenControl} />}
      {activeTab === 'location' && <LocationTab deviceId={device.id} state={state} />}
      {activeTab === 'apps' && <AppsTab deviceId={device.id} state={state} readOnly={readOnly} />}
      {activeTab === 'policies' && <PoliciesTab key={device.id} deviceId={device.id} state={state} readOnly={readOnly} />}
      {activeTab === 'commands' && <CommandsTab deviceId={device.id} readOnly={readOnly} />}
      {activeTab === 'events' && <EventsTab deviceId={device.id} />}
      {activeTab === 'files' && <FilesTab deviceId={device.id} readOnly={readOnly} />}
      {activeTab === 'settings' && (
        <div className="space-y-4">
          <EditDeviceSection device={device} deviceType="mobile" />
          <LogsSection device={device} />
          <DangerZoneSection device={device} />
        </div>
      )}

      <ConfirmModal
        isOpen={modal === 'reboot'}
        onClose={() => setModal(null)}
        title="Reiniciar tablet"
        message={`Reiniciar ${device.name} agora? Quem estiver usando perde o que não salvou.`}
        confirmText="Reiniciar"
        cancelText="Cancelar"
        onConfirm={() => run('reboot', null, 'Reinício solicitado.')}
      />

      <FieldsModal
        isOpen={modal === 'message'}
        onClose={() => setModal(null)}
        title="Enviar mensagem para a tela"
        fields={MESSAGE_FIELDS}
        loading={cmd.isPending}
        onSubmit={(v) => run('message', messagePayload(v), v.requireAck
          ? 'Mensagem enviada. A hora da leitura aparece em Comandos quando tocarem em "Ciente".'
          : 'Mensagem enviada.')}
      />

      <LocalCodeModal
        isOpen={modal === 'code'}
        onClose={() => setModal(null)}
        deviceId={device.id}
        deviceName={device.name}
        allowRemove={allowRemove}
      />

      <FieldsModal
        isOpen={modal === 'wipe'}
        onClose={() => setModal(null)}
        title="Apagar o tablet (restauração de fábrica)"
        description={
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800">
            <Trash2 className="w-5 h-5 flex-shrink-0" />
            <span>
              Todos os dados do tablet serão apagados e ele sai do MDM. Para voltar a gerenciá-lo é preciso
              ler o QR de adesão de novo. Esta ação não pode ser desfeita e pede o código do autenticador.
            </span>
          </div>
        }
        fields={[{ key: 'reason', label: 'Motivo', placeholder: 'ex.: tablet extraviado' }]}
        confirmMatch={device.name}
        submitLabel="Apagar tablet"
        danger
        loading={wipe.isPending}
        onSubmit={runWipe}
      />
    </div>
  )
}

function QuickAction({ icon: Icon, label, onClick, disabled, danger, highlight, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border disabled:opacity-50',
        danger ? 'border-red-300 text-red-700 hover:bg-red-50'
          : highlight ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
      )}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  )
}
